import {
  type DEFAULT_REAPPRO_STORE_CATEGORY_PARAMS,
  getErrorMessageFromPossibleBlob,
  updateReapproCategoryAlgorithms,
  useProducts,
  useReapproAlgorithmMatrixForm,
  useReapproCategories,
  useReapproCategoryAlgorithms,
  useReapproSimulationExport,
  useStores,
} from '@gozoki/api'
import type { ReapproAlgorithm, ReaproPeriode } from '@gozoki/api-types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ChevronLeft, FileSpreadsheet, Store } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { ReapproCategoryAccordion } from '~/components/routes/categories/ReapproCategoryAccordion'
import { Button } from '~/components/ui/button'
import { downloadBlob } from '../../../utils/excel/downloadBlob'

import { withResourcePermission } from '../../../components/RequireResourcePermission'
export const Route = createFileRoute('/_authenticated/categories/reappro/config')({
  component: withResourcePermission('categories', 'read')(AlgorithmConfigPage),
})

const getAlgorithmKey = (categoryId: number, storeId: number) => `${categoryId}-${storeId}`

const DEFAULT_PARAMS: typeof DEFAULT_REAPPRO_STORE_CATEGORY_PARAMS = {
  algorithm: 'VMAX' as ReapproAlgorithm,
  periode: '5J' as ReaproPeriode,
  coefSecu: 1,
  coefWeekend: 1,
  coverageRate: null,
}

const slugifyStoreLabel = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replaceAll(/[\u0300-\u036f]/g, '')
    .replaceAll(/[^a-z0-9]+/g, '-')
    .replaceAll(/^-+|-+$/g, '')

const getStoreSimulationFilename = (
  store?: {
    id: number
    label: string
    address?: { postalCode?: string }
  } | null,
) => {
  if (!store) {
    return 'reappro-simulation.xlsx'
  }

  const cp = (store.address?.postalCode ?? '').trim().slice(0, 2).toLowerCase()
  const labelSlug = slugifyStoreLabel(store.label)
  const segment = [cp, labelSlug].filter((part) => part.length > 0).join('-')

  if (segment.length === 0) {
    return `reappro-simulation-store-${store.id}.xlsx`
  }

  return `reappro-simulation-${segment}.xlsx`
}

function AlgorithmConfigPage() {
  const navigate = Route.useNavigate()
  const queryClient = useQueryClient()

  const { data: allStores = [] } = useStores()
  const boutiques = useMemo(
    () => allStores.filter((store) => store.storeType === 'BOUTIQUE'),
    [allStores],
  )

  const { data: reapproCategories = [], isLoading: isCategoriesLoading } = useReapproCategories()
  const { data: products = [], isLoading: isProductsLoading } = useProducts()
  const { data: algorithms = [], isLoading: isAlgorithmsLoading } = useReapproCategoryAlgorithms()
  const sortedReapproCategories = useMemo(
    () =>
      [...reapproCategories].sort((a, b) =>
        a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' }),
      ),
    [reapproCategories],
  )

  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null)

  useEffect(() => {
    if (boutiques.length > 0 && selectedStoreId === null) {
      setSelectedStoreId(boutiques[0].id)
    }
  }, [boutiques, selectedStoreId])

  const selectedStore = boutiques.find((b) => b.id === selectedStoreId)

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { isDirty },
  } = useReapproAlgorithmMatrixForm()

  const isLoading = isCategoriesLoading || isAlgorithmsLoading || isProductsLoading

  useEffect(() => {
    if (isLoading) return
    const nextState: Record<string, typeof DEFAULT_PARAMS> = {}
    algorithms.forEach((entry) => {
      nextState[getAlgorithmKey(entry.reapproCategoryId, entry.storeId)] = {
        algorithm: entry.algorithm,
        periode: entry.periode,
        coefSecu: entry.coefSecu,
        coefWeekend: entry.coefWeekend,
        coverageRate: entry.coverageRate ?? null,
      }
    })
    reset({ params: nextState }, { keepDirty: false })
  }, [algorithms, isLoading, reset])

  const updateAlgorithmsMut = useMutation({
    mutationFn: updateReapproCategoryAlgorithms,
    onSuccess: () => {
      toast.success('Algorithmes de réappro mis à jour')
      queryClient.invalidateQueries({ queryKey: ['reapproCategoryAlgorithms'] })
      reset(getValues(), { keepDirty: false })
    },
    onError: () => {
      toast.error("Impossible d'enregistrer les algorithmes de réappro")
    },
  })
  const { mutate: exportReapproSimulation, isPending: isExportingReapproSimulation } =
    useReapproSimulationExport()

  const handleSave = handleSubmit(() => {
    if (selectedStoreId === null) return

    const payload = sortedReapproCategories.map((category) => {
      const key = getAlgorithmKey(category.id, selectedStoreId)
      const p = getValues(`params.${key}`) ?? DEFAULT_PARAMS
      return {
        storeId: selectedStoreId,
        reapproCategoryId: category.id,
        algorithm: (p.algorithm ?? DEFAULT_PARAMS.algorithm) as ReapproAlgorithm,
        periode: (p.periode ?? DEFAULT_PARAMS.periode) as ReaproPeriode,
        coefSecu: p.coefSecu ?? DEFAULT_PARAMS.coefSecu,
        coefWeekend: p.coefWeekend ?? DEFAULT_PARAMS.coefWeekend,
        coverageRate: p.coverageRate ?? null,
      }
    })
    updateAlgorithmsMut.mutate(payload)
  })

  const totalProducts = useMemo(() => products.filter((p) => p.isActive).length, [products])

  const handleExportSimulationXlsx = () => {
    if (!selectedStoreId) {
      toast.error('Aucune boutique sélectionnée')
      return
    }

    exportReapproSimulation(selectedStoreId, {
      onError: async (error) => {
        const errorMessage = await getErrorMessageFromPossibleBlob(error)
        toast.error('Impossible de générer la simulation Excel', {
          description: errorMessage,
        })
      },
      onSuccess: (blob) => {
        downloadBlob({
          blob,
          filename: getStoreSimulationFilename(selectedStore),
        })
        toast.success('Export généré', {
          description: `Simulation complète — ${selectedStore?.label ?? ''} — ${totalProducts} produits exportés.`,
        })
      },
    })
  }

  let categoriesContent = null
  if (isLoading) {
    categoriesContent = (
      <div className="text-center py-12 text-sm text-muted-foreground">Chargement...</div>
    )
  } else if (selectedStoreId !== null) {
    categoriesContent = sortedReapproCategories.map((category) => (
      <ReapproCategoryAccordion
        key={`${category.id}-${selectedStoreId}`}
        category={category}
        storeId={selectedStoreId}
        control={control}
      />
    ))
  }

  return (
    <>
      {/* Sticky top bar */}
      <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-border">
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={() => navigate({ to: '/categories', search: { tab: 'reappro' } })}
          >
            <ChevronLeft />
          </Button>
          <div>
            <h1 className="text-lg">Configuration des algorithmes</h1>
            {selectedStore && (
              <p className="text-sm text-muted-foreground">
                Gérez les règles de réapprovisionnement pour{' '}
                <span className="text-foreground font-medium">{selectedStore.label}</span>
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 border border-border rounded-lg px-3 py-2">
            <Store size={14} className="text-primary shrink-0" />
            <select
              value={selectedStoreId ?? ''}
              onChange={(e) => setSelectedStoreId(Number(e.target.value))}
              className="text-sm bg-transparent focus:outline-none min-w-50or-pointer"
            >
              {boutiques.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.label}
                </option>
              ))}
            </select>
          </div>
          {isDirty && (
            <Button
              type="submit"
              form="algorithm-config-form"
              disabled={updateAlgorithmsMut.isPending}
            >
              Enregistrer
            </Button>
          )}
        </div>
      </div>

      {/* Page content */}
      <form id="algorithm-config-form" onSubmit={handleSave}>
        <div className="bg-muted/20 min-h-screen">
          <div className="max-w-300 mx-auto p-6 space-y-4">
            {/* Summary bar */}
            <div className="flex items-center justify-between bg-background rounded-xl border border-border px-5 py-3 shadow-sm">
              <div className="flex items-center gap-3">
                <Store size={16} className="text-primary" />
                <span className="text-sm">{selectedStore?.label}</span>
                <span className="text-xs text-muted-foreground">
                  — {sortedReapproCategories.length} catégories de réapprovisionnement
                </span>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-xs text-muted-foreground">{totalProducts} produits</span>
                <Button
                  type="button"
                  onClick={handleExportSimulationXlsx}
                  disabled={!selectedStoreId || isExportingReapproSimulation}
                >
                  <FileSpreadsheet />
                  {isExportingReapproSimulation ? 'Génération...' : 'Simulation Excel'}
                </Button>
              </div>
            </div>

            {/* Category accordions */}
            {categoriesContent}
          </div>
        </div>
      </form>
    </>
  )
}
